import { Box } from "@mui/material";
import { DisclosureHost } from "../../../../../../../../hooks/use-disclosure";
import StyledFormModal from "../../../../../../../../components/globalModal/StyledModal";
import Flex from "../../../../../../../../components/Flex";
import Text from "../../../../../../../../components/Text";

interface EligibilityReportModalProps {
  disclosureHost: DisclosureHost;
}

// Data structure for easy API mapping
// To integrate with API:
// 1. Replace the eligibilityData object with API response data
// 2. Update the interface to match your API response structure
// 3. The component will automatically render all fields from the data object
interface EligibilityData {
  eligibilityDetails: {
    lastChecked: string;
    patientInfo: {
      patientName: string;
      dob: string;
      gender: string;
      address: {
        street: string;
        city: string;
        state: string;
        zipCode: string;
      };
    };
    policyHolderInfo: {
      name: string;
      relation: string;
      dob: string;
      gender: string;
      address: string;
    };
  };
  insuranceDetails: {
    planName: string;
    planNumber: string;
    planEffectiveDates: string;
    serviceTypeCode: string;
    groupNumber: string;
    groupName: string;
    policyName: string;
    policyNumber: string;
  };
  copayDetails: {
    services: Array<{
      serviceType: string;
      inNetwork: string;
      coverageLevel: string;
      amount: string;
      message: string;
      authRequired: string;
      facilityType: string;
    }>;
  };
}

const EligibilityReportModal = ({
  disclosureHost,
}: EligibilityReportModalProps) => {
  // Sample data - replace with API data
  const eligibilityData: EligibilityData = {
    eligibilityDetails: {
      lastChecked: "12 Dec 2022",
      patientInfo: {
        patientName: "Venessa Earle",
        dob: "27 Nov 1965",
        gender: "Female",
        address: {
          street: "2436 Naples Avenue, Panama City",
          city: "Panama City",
          state: "FL",
          zipCode: "32405",
        },
      },
      policyHolderInfo: {
        name: "Corina Earle",
        relation: "Self",
        dob: "27 Nov 1965",
        gender: "Female",
        address: "2436 Naples Avenue, Panama City FL 32405",
      },
    },
    insuranceDetails: {
      planName: "Advance Pharmacy",
      planNumber: "25645251645",
      planEffectiveDates: "01-01-2024 to 30-12-2024",
      serviceTypeCode: "39",
      groupNumber: "00122345",
      groupName: "ACME,INC.",
      policyName: "-",
      policyNumber: "-",
    },
    copayDetails: {
      services: [
        {
          serviceType: "Orthopedic [BK] - (2)",
          inNetwork: "Yes",
          coverageLevel: "Individual",
          amount: "$ 25",
          message:
            "SPECIALIST - Virtual Care Included for Specific Services\nPCP - Virtual Care Included for Specific Services\nPCP - Virtual Care Included for Specific Services",
          authRequired: "No",
          facilityType: "Office",
        },
        {
          serviceType: "Gastrointestinal [BN] - (4)",
          inNetwork: "Yes",
          coverageLevel: "Individual",
          amount: "$ 25",
          message:
            "SPECIALIST - Virtual Care Included for Specific Services\nPCP - Virtual Care Included for Specific Services",
          authRequired: "No",
          facilityType: "Office",
        },
      ],
    },
  };

  // Helper function to render field row
  const renderFieldRow = (
    label: string,
    value: string | React.ReactNode,
    key: string
  ) => (
    <Flex key={key} alignItems="center" gap={0.25}>
      <Box
        sx={{
          width: "120px",
        }}
      >
        <Text fontSize="12px" color="#74797B" fontWeight={500}>
          {label}
        </Text>
      </Box>
      <Box
        sx={{
          width: "20px",
        }}
      >
        <Text fontSize="12px" color="#373D41" fontWeight={500}>
          :
        </Text>
      </Box>
      <Box
        sx={{
          flex: 1,
        }}
      >
        {typeof value === "string" ? (
          <Text fontSize="12px" color="#373D41" fontWeight={500}>
            {value}
          </Text>
        ) : (
          value
        )}
      </Box>
    </Flex>
  );

  // Helper function to render address field
  const renderAddressField = (
    label: string,
    address: { street: string; city: string; state: string; zipCode: string },
    key: string
  ) => (
    <Flex key={key} alignItems="flex-start" gap={0.25}>
      <Box
        sx={{
          width: "120px",
        }}
      >
        <Text fontSize="12px" color="#74797B" fontWeight={500}>
          {label}
        </Text>
      </Box>
      <Box
        sx={{
          width: "20px",
        }}
      >
        <Text fontSize="12px" color="#373D41" fontWeight={500}>
          :
        </Text>
      </Box>
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          gap: 0.25,
        }}
      >
        <Text fontSize="12px" color="#373D41" fontWeight={500}>
          {address.street}
        </Text>
        <Text fontSize="12px" color="#373D41" fontWeight={500}>
          {address.state} {address.zipCode}
        </Text>
      </Box>
    </Flex>
  );

  const ReportDetails = () => {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 3,
          width: "100%",
          p: 1.5,
        }}
      >
        {/* Eligibility Details Section */}
        <Box
          sx={{
            bgcolor: "white",
            borderRadius: 1,
            p: 1.5,
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
            width: "100%",
            border: "1px solid #E7E7E7",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Text fontSize="14px" color="#21262B" fontWeight={500}>
              Eligibility Details
            </Text>
            <Text fontSize="12px" color="#74797B" fontWeight={500}>
              (Last Checked {eligibilityData.eligibilityDetails.lastChecked})
            </Text>
          </Box>

          {/* Grid Layout - 2 Columns */}
          <Box
            sx={{
              display: "flex",
              gap: 1.5,
            }}
          >
            {/* Left Column - Patient Information */}
            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                gap: 1,
              }}
            >
              {renderFieldRow(
                "Patient Name",
                eligibilityData.eligibilityDetails.patientInfo.patientName,
                "patient-name"
              )}
              {renderFieldRow(
                "DOB",
                eligibilityData.eligibilityDetails.patientInfo.dob,
                "patient-dob"
              )}
              {renderFieldRow(
                "Gender",
                eligibilityData.eligibilityDetails.patientInfo.gender,
                "patient-gender"
              )}
              {renderAddressField(
                "Address",
                eligibilityData.eligibilityDetails.patientInfo.address,
                "patient-address"
              )}
            </Box>

            {/* Right Column - Policy Holder Information */}
            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                gap: 1,
              }}
            >
              {renderFieldRow(
                "Policy Holder Name",
                eligibilityData.eligibilityDetails.policyHolderInfo.name,
                "policy-holder-name"
              )}
              {renderFieldRow(
                "Patient Relation",
                eligibilityData.eligibilityDetails.policyHolderInfo.relation,
                "patient-relation"
              )}
              {renderFieldRow(
                "DOB",
                eligibilityData.eligibilityDetails.policyHolderInfo.dob,
                "policy-holder-dob"
              )}
              {renderFieldRow(
                "Gender",
                eligibilityData.eligibilityDetails.policyHolderInfo.gender,
                "policy-holder-gender"
              )}
              {renderFieldRow(
                "Address",
                eligibilityData.eligibilityDetails.policyHolderInfo.address,
                "policy-holder-address"
              )}
            </Box>
          </Box>
        </Box>

        {/* Insurance Details Section */}
        <Box
          sx={{
            bgcolor: "white",
            borderRadius: 1,
            p: 1.5,
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
            width: "100%",
            border: "1px solid #E7E7E7",
          }}
        >
          <Text fontSize="14px" color="#21262B" fontWeight={500}>
            Insurance Details
          </Text>

          {/* Grid Layout - 2 Columns */}
          <Box
            sx={{
              display: "flex",
              gap: 1.5,
            }}
          >
            {/* Left Column */}
            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                gap: 1,
              }}
            >
              {renderFieldRow(
                "Plan Name",
                eligibilityData.insuranceDetails.planName,
                "plan-name"
              )}
              {renderFieldRow(
                "Plan Number",
                eligibilityData.insuranceDetails.planNumber,
                "plan-number"
              )}
              {renderFieldRow(
                "Plan Effective Dates",
                eligibilityData.insuranceDetails.planEffectiveDates,
                "plan-effective-dates"
              )}
              {renderFieldRow(
                "Service Type Code",
                eligibilityData.insuranceDetails.serviceTypeCode,
                "service-type-code"
              )}
            </Box>

            {/* Right Column */}
            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                gap: 1,
              }}
            >
              {renderFieldRow(
                "Group Number",
                eligibilityData.insuranceDetails.groupNumber,
                "group-number"
              )}
              {renderFieldRow(
                "Group Name",
                eligibilityData.insuranceDetails.groupName,
                "group-name"
              )}
              {renderFieldRow(
                "Policy Name",
                eligibilityData.insuranceDetails.policyName,
                "policy-name"
              )}
              {renderFieldRow(
                "Policy Number",
                eligibilityData.insuranceDetails.policyNumber,
                "policy-number"
              )}
            </Box>
          </Box>
        </Box>

        {/* Copay Section */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 0.75,
            width: "100%",
          }}
        >
          <Text fontSize="14px" color="#21262B" fontWeight={500}>
            Copay
          </Text>

          {/* Copay Table */}
          <Box
            sx={{
              border: "1px solid #E7E7E7",
              borderRadius: 1,
              overflow: "hidden",
              width: "100%",
            }}
          >
            {/* Table Header */}
            <Flex bgcolor="#F8F8F8" borderBottom="1px solid #E7E7E7">
              <Box
                sx={{
                  width: "80px",
                  p: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRight: "1px solid #E7E7E7",
                }}
              >
                <Text fontSize="12px" color="#595F63" fontWeight={500}>
                  In Network
                </Text>
              </Box>
              <Box
                sx={{
                  width: "110px",
                  p: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRight: "1px solid #E7E7E7",
                }}
              >
                <Text fontSize="12px" color="#595F63" fontWeight={500}>
                  Coverage Level
                </Text>
              </Box>
              <Box
                sx={{
                  width: "64px",
                  p: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRight: "1px solid #E7E7E7",
                }}
              >
                <Text fontSize="12px" color="#595F63" fontWeight={500}>
                  Amount
                </Text>
              </Box>
              <Box
                sx={{
                  flex: 1,
                  p: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRight: "1px solid #E7E7E7",
                }}
              >
                <Text fontSize="12px" color="#595F63" fontWeight={500}>
                  Message
                </Text>
              </Box>
              <Box
                sx={{
                  width: "132px",
                  p: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRight: "1px solid #E7E7E7",
                }}
              >
                <Text fontSize="12px" color="#595F63" fontWeight={500}>
                  Auth/Cert Required
                </Text>
              </Box>
              <Box
                sx={{
                  width: "94px",
                  p: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Text fontSize="12px" color="#595F63" fontWeight={500}>
                  Facility Type
                </Text>
              </Box>
            </Flex>

            {/* Service Rows - Mapped from data */}
            {eligibilityData.copayDetails.services.map((service, index) => (
              <Box
                key={index}
                sx={{
                  borderBottom: "1px solid #E7E7E7",
                }}
              >
                <Box
                  sx={{
                    p: 1.5,
                    bgcolor: "#21262B",
                    width: "100%",
                  }}
                >
                  <Text fontSize="12px" color="white" fontWeight={500}>
                    {service.serviceType}
                  </Text>
                </Box>
                <Flex>
                  <Box
                    sx={{
                      width: "80px",
                      p: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRight: "1px solid #E7E7E7",
                      height: "134px",
                    }}
                  >
                    <Text fontSize="12px" color="#373D41" fontWeight={400}>
                      {service.inNetwork}
                    </Text>
                  </Box>
                  <Box
                    sx={{
                      width: "110px",
                      p: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRight: "1px solid #E7E7E7",
                      height: "134px",
                    }}
                  >
                    <Text fontSize="12px" color="#373D41" fontWeight={400}>
                      {service.coverageLevel}
                    </Text>
                  </Box>
                  <Box
                    sx={{
                      width: "64px",
                      p: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRight: "1px solid #E7E7E7",
                      height: "134px",
                      bgcolor: "#FFFBF2",
                    }}
                  >
                    <Text fontSize="12px" color="#373D41" fontWeight={400}>
                      {service.amount}
                    </Text>
                  </Box>
                  <Box
                    sx={{
                      flex: 1,
                      p: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRight: "1px solid #E7E7E7",
                      height: "134px",
                    }}
                  >
                    <Text
                      fontSize="12px"
                      color="#373D41"
                      fontWeight={500}
                      lineHeight={1.6}
                    >
                      {service.message.split("\n").map((line, i) => (
                        <span key={i}>
                          {line}
                          {i < service.message.split("\n").length - 1 && <br />}
                        </span>
                      ))}
                    </Text>
                  </Box>
                  <Box
                    sx={{
                      width: "132px",
                      p: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRight: "1px solid #E7E7E7",
                      height: "134px",
                    }}
                  >
                    <Text fontSize="12px" color="#373D41" fontWeight={400}>
                      {service.authRequired}
                    </Text>
                  </Box>
                  <Box
                    sx={{
                      width: "94px",
                      p: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "134px",
                    }}
                  >
                    <Text fontSize="12px" color="#373D41" fontWeight={400}>
                      {service.facilityType}
                    </Text>
                  </Box>
                </Flex>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <StyledFormModal
      open={disclosureHost.opened}
      onClose={() => disclosureHost.close()}
      dialogTitle="Eligibility Report"
      sx={{
        "& .MuiDialog-paper": {
          width: "50vw",
          height: "60vh",
        },
      }}
    >
      <Box
        sx={{
          pt: 2,
        }}
      >
        <ReportDetails />
      </Box>
    </StyledFormModal>
  );
};

export default EligibilityReportModal;
